import React, { useState } from 'react';
import {
  Plus, X, Search, Leaf, Beef, Fish, Wheat, Droplet,
  Flame, Heart, Star, AlertTriangle
} from 'lucide-react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

// ── Diet styles matching iOS HWDietStyle ─────────────────────────────────────
const DIET_STYLES = [
  { value: 'omnivore',      label: 'Omnivore',      description: 'Eats everything',        icon: Beef    },
  { value: 'vegetarian',    label: 'Vegetarian',    description: 'No meat',                icon: Leaf    },
  { value: 'vegan',         label: 'Vegan',         description: 'No animal products',     icon: Leaf    },
  { value: 'pescatarian',   label: 'Pescatarian',   description: 'Fish but no meat',       icon: Fish    },
  { value: 'keto',          label: 'Keto',          description: 'Low carb, high fat',     icon: Flame   },
  { value: 'paleo',         label: 'Paleo',         description: 'Whole foods',            icon: Wheat   },
  { value: 'halal',         label: 'Halal',         description: 'Islamic dietary laws',   icon: Heart   },
  { value: 'kosher',        label: 'Kosher',        description: 'Jewish dietary laws',    icon: Star    },
  { value: 'glutenFree',    label: 'Gluten-Free',   description: 'No gluten',              icon: Wheat   },
  { value: 'dairyFree',     label: 'Dairy-Free',    description: 'No dairy',               icon: Droplet },
  { value: 'lowCarb',       label: 'Low Carb',      description: 'Reduced carbs',          icon: Flame   },
  { value: 'mediterranean', label: 'Mediterranean', description: 'Mediterranean diet',     icon: Leaf    },
];

const COMMON_ALLERGENS = [
  'Peanuts', 'Tree Nuts', 'Milk', 'Eggs',
  'Soy', 'Wheat', 'Fish', 'Shellfish', 'Sesame',
];

const DietarySetup = ({ data, onChange, onContinue, onSkip }) => {
  const [customAllergen,      setCustomAllergen]      = useState('');
  const [showCustomInput,     setShowCustomInput]      = useState(false);
  const [avoidSearch,         setAvoidSearch]          = useState('');
  const [avoidResults,        setAvoidResults]         = useState([]);
  const [avoidSearchLoading,  setAvoidSearchLoading]   = useState(false);
  const [showAvoidSearch,     setShowAvoidSearch]      = useState(false);

  const setDietStyle   = (value) => onChange({ ...data, dietStyle: data.dietStyle === value ? null : value });
  const toggleAllergen = (a)     => {
    const next = new Set(data.allergies);
    next.has(a) ? next.delete(a) : next.add(a);
    onChange({ ...data, allergies: next });
  };
  const removeAllergen = (a) => {
    const next = new Set(data.allergies);
    next.delete(a);
    onChange({ ...data, allergies: next });
  };
  const addCustomAllergen = () => {
    const t = customAllergen.trim();
    if (!t) return;
    const next = new Set(data.allergies);
    next.add(t);
    onChange({ ...data, allergies: next });
    setCustomAllergen('');
    setShowCustomInput(false);
  };
  const removeAvoid = (item) => {
    const next = new Set(data.avoid);
    next.delete(item);
    onChange({ ...data, avoid: next });
  };
  const addAvoid = (item) => {
    const next = new Set(data.avoid);
    next.add(item);
    onChange({ ...data, avoid: next });
    setAvoidSearch('');
    setAvoidResults([]);
    setShowAvoidSearch(false);
  };

  const searchAvoid = async (q) => {
    setAvoidSearch(q);
    if (q.length < 2) { setAvoidResults([]); return; }
    setAvoidSearchLoading(true);
    try {
      const lower = q.toLowerCase();
      const snap  = await getDocs(
        query(collection(db, 'ingredients'),
          where('nameLowercase', '>=', lower),
          where('nameLowercase', '<=', lower + '\uf8ff'),
          limit(20))
      );
      setAvoidResults(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    } finally {
      setAvoidSearchLoading(false);
    }
  };

  const customAllergens = [...data.allergies].filter(a => !COMMON_ALLERGENS.includes(a));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D5F4D]/8 via-[#8B6F47]/5 to-[#F5F3EF]">
      <div className="max-w-2xl mx-auto px-lg pb-40">

        {/* Header */}
        <div className="text-center pt-xxxl mb-xxxl">
          <div className="w-16 h-16 rounded-full bg-[#2D5F4D] flex items-center justify-center mx-auto mb-lg">
            <Leaf size={32} className="text-white" />
          </div>
          <h1 className="text-[30px] font-serif text-[#2D5F4D] mb-sm" style={{ fontFamily: 'Georgia, serif' }}>
            Dietary Preferences
          </h1>
          <p className="text-callout text-[#8B7355]">Help us personalize your experience</p>
        </div>

        <div className="space-y-xxxl">

          {/* ── Diet Style ── */}
          <section>
            <SectionTitle title="Diet Style" subtitle="Select your primary dietary preference" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-md mt-md">
              {DIET_STYLES.map(({ value, label, description, icon: Icon }) => {
                const selected = data.dietStyle === value;
                return (
                  <button
                    key={value}
                    onClick={() => setDietStyle(value)}
                    className={`flex flex-col items-center gap-sm p-lg rounded-2xl border-2 transition-all text-center ${
                      selected
                        ? 'border-[#2D5F4D] bg-[#2D5F4D]/5'
                        : 'border-[#2D5F4D]/10 bg-white hover:border-[#2D5F4D]/30'
                    }`}
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      selected ? 'bg-[#2D5F4D]/15' : 'bg-[#F5F3EF]'
                    }`}>
                      <Icon size={22} className={selected ? 'text-[#2D5F4D]' : 'text-[#8B7355]'} />
                    </div>
                    <p className={`text-callout font-semibold ${selected ? 'text-[#2D5F4D]' : 'text-[#2D5F4D]'}`}>
                      {label}
                    </p>
                    <p className="text-caption text-[#8B7355] leading-tight">{description}</p>
                  </button>
                );
              })}
            </div>
          </section>

          <Divider />

          {/* ── Allergies ── */}
          <section>
            <SectionTitle title="Allergies" subtitle="Select all allergens you need to avoid" />
            <div className="flex flex-wrap gap-sm mt-md">
              {COMMON_ALLERGENS.map(a => {
                const selected = data.allergies.has(a);
                return (
                  <button
                    key={a}
                    onClick={() => toggleAllergen(a)}
                    className={`flex items-center gap-xs px-md py-sm rounded-full border-2 text-caption font-semibold transition-all ${
                      selected
                        ? 'bg-red-50 text-red-600 border-red-200'
                        : 'bg-white text-[#2D5F4D] border-[#2D5F4D]/20 hover:border-[#2D5F4D]'
                    }`}
                  >
                    {selected && <span className="text-xs">✓</span>}
                    {a}
                  </button>
                );
              })}
            </div>

            {/* Custom allergens */}
            {customAllergens.length > 0 && (
              <div className="flex flex-wrap gap-sm mt-md">
                {customAllergens.map(a => (
                  <RemovableChip key={a} label={a} color="red" onRemove={() => removeAllergen(a)} />
                ))}
              </div>
            )}

            {/* Add custom */}
            {showCustomInput ? (
              <div className="flex gap-sm mt-md">
                <input
                  type="text"
                  value={customAllergen}
                  onChange={e => setCustomAllergen(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addCustomAllergen()}
                  placeholder="e.g. Mustard"
                  autoFocus
                  className="flex-1 px-md py-sm rounded-2xl bg-white border border-[#2D5F4D]/20 text-callout text-[#2D5F4D] outline-none focus:border-[#2D5F4D]/40"
                />
                <button
                  onClick={addCustomAllergen}
                  className="px-md py-sm rounded-2xl bg-[#2D5F4D] text-white text-caption font-semibold"
                >
                  Add
                </button>
                <button
                  onClick={() => { setShowCustomInput(false); setCustomAllergen(''); }}
                  className="px-md py-sm rounded-2xl bg-white border border-[#2D5F4D]/20 text-caption text-[#8B7355]"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCustomInput(true)}
                className="flex items-center gap-xs mt-md text-callout text-red-500 font-semibold hover:text-red-600 transition-colors"
              >
                <Plus size={18} />
                Add Custom Allergen
              </button>
            )}
          </section>

          <Divider />

          {/* ── Ingredients to Avoid ── */}
          <section>
            <SectionTitle title="Ingredients to Avoid" subtitle="Search and add ingredients you prefer not to eat" />

            {data.avoid.size > 0 && (
              <div className="flex flex-wrap gap-sm mt-md">
                {[...data.avoid].map(item => (
                  <RemovableChip key={item} label={item} color="brown" onRemove={() => removeAvoid(item)} />
                ))}
              </div>
            )}

            {/* Search */}
            {showAvoidSearch ? (
              <div className="mt-md">
                <div className="relative">
                  <Search size={18} className="absolute left-md top-1/2 -translate-y-1/2 text-[#2D5F4D]/40" />
                  <input
                    type="text"
                    value={avoidSearch}
                    onChange={e => searchAvoid(e.target.value)}
                    placeholder="Search ingredients…"
                    autoFocus
                    className="w-full pl-12 pr-lg py-md rounded-2xl bg-white border border-[#2D5F4D]/20 text-callout text-[#2D5F4D] outline-none focus:border-[#2D5F4D]/40"
                  />
                </div>

                {avoidSearch.length >= 2 && (
                  <div className="mt-sm bg-white rounded-2xl border border-[#2D5F4D]/10 overflow-hidden shadow-sm max-h-48 overflow-y-auto">
                    {avoidSearchLoading ? (
                      <p className="text-center text-callout text-[#8B7355] py-lg">Searching…</p>
                    ) : avoidResults.length === 0 ? (
                      <p className="text-center text-callout text-[#8B7355] py-lg">No ingredients found</p>
                    ) : (
                      avoidResults.map(ing => (
                        <button
                          key={ing.id}
                          onClick={() => addAvoid(ing.name)}
                          disabled={data.avoid.has(ing.name)}
                          className={`w-full text-left px-lg py-md border-b border-[#2D5F4D]/05 last:border-b-0 transition-all ${
                            data.avoid.has(ing.name)
                              ? 'opacity-40 cursor-not-allowed'
                              : 'hover:bg-[#2D5F4D]/5'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-callout font-semibold text-[#2D5F4D]">{ing.name}</p>
                            {ing.isAllergen && <AlertTriangle size={16} className="text-red-500" />}
                          </div>
                          {ing.usageCount > 0 && (
                            <p className="text-caption text-[#8B7355]">{ing.usageCount} products</p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}

                <button
                  onClick={() => { setShowAvoidSearch(false); setAvoidSearch(''); setAvoidResults([]); }}
                  className="mt-sm text-caption text-[#8B7355] hover:text-[#2D5F4D] transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAvoidSearch(true)}
                className="flex items-center gap-xs mt-md text-callout text-[#2D5F4D] font-semibold hover:text-[#2D5F4D]/70 transition-colors"
              >
                <Search size={18} />
                Search Ingredients to Avoid
              </button>
            )}
          </section>

        </div>
      </div>

      {/* ── Fixed bottom bar (mirrors iOS safeAreaInset) ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-[#2D5F4D]/10 py-lg px-lg">
        <div className="max-w-2xl mx-auto space-y-sm">
          <button
            onClick={onContinue}
            className="w-full py-lg rounded-full bg-[#2D5F4D] text-white font-semibold text-callout hover:bg-[#2D5F4D]/90 transition-all"
          >
            Continue
          </button>
          <button
            onClick={onSkip}
            className="w-full py-md text-[#8B7355] font-medium text-callout hover:text-[#2D5F4D] transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle = ({ title, subtitle }) => (
  <div>
    <h2 className="text-[20px] font-semibold text-[#2D5F4D]">{title}</h2>
    <p className="text-caption text-[#8B7355] mt-xs">{subtitle}</p>
  </div>
);

const Divider = () => (
  <div className="h-px bg-[#2D5F4D]/8" />
);

const RemovableChip = ({ label, color, onRemove }) => {
  const colors = {
    red:   'bg-red-50 text-red-600 border-red-200',
    brown: 'bg-[#8B7355]/10 text-[#8B7355] border-[#8B7355]/30',
  };
  return (
    <div className={`flex items-center gap-xs px-md py-sm rounded-full border text-caption font-semibold ${colors[color]}`}>
      {label}
      <button onClick={onRemove} className="hover:opacity-70 transition-opacity">
        <X size={14} />
      </button>
    </div>
  );
};

export default DietarySetup;